import React from "react";
import { Target, ArrowsLeftRight, House, Airplane, Trophy } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";

function Rule({ icon: Icon, title, desc, pts }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#02371b] text-white flex items-center justify-center flex-shrink-0">
        <Icon size={22} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-base text-[#02371b]">{title}</h3>
          <span className="font-mono-tab text-sm font-bold text-[#41a42b] whitespace-nowrap">+{pts}</span>
        </div>
        <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function Rules() {
  const { t } = useI18n();
  const { activeGroup } = useActiveGroup();
  
  const isFourPoint = activeGroup?.scoring_system === "four_point";

  return (
    <div className="px-4 pt-4 pb-10" data-testid="rules-page">
      <div className="mb-5">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral-400">{t("scoring")}</div>
        <h1 className="font-display text-3xl text-[#02371b] leading-none mt-1">{t("rules_title")}</h1>
      </div>

      <div className="grain relative rounded-2xl bg-[#02371b] text-white p-5 mb-4 overflow-hidden">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50">{t("max_haul")}</div>
        <div className="font-mono-tab text-5xl font-bold mt-1">{isFourPoint ? 4 : 5} <span className="text-lg text-white/40">{t("of")} {isFourPoint ? 4 : 5}</span></div>
        <div className="text-xs text-white/60 mt-3 leading-relaxed">
          <span dangerouslySetInnerHTML={{ __html: isFourPoint ? t("rules_max_haul_four_desc") : t("rules_max_haul_desc") }} />
        </div>
      </div>

      {/* Playoffs Double Points Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3 mb-4 text-amber-800">
        <Trophy size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed">
          <strong className="block font-bold mb-0.5">{t("rules_playoffs_double_title")}</strong>
          {t("rules_playoffs_double_desc")}
        </div>
      </div>

      <div className="space-y-3">
        {isFourPoint ? (
          <>
            <Rule icon={House} title={t("rule_four_home")} desc={t("rule_four_home_desc")} pts={1} />
            <Rule icon={Airplane} title={t("rule_four_away")} desc={t("rule_four_away_desc")} pts={1} />
            <Rule icon={ArrowsLeftRight} title={t("rule_four_diff")} desc={t("rule_four_diff_desc")} pts={1} />
            <Rule icon={Target} title={t("rule_four_winner")} desc={t("rule_four_winner_desc")} pts={1} />
          </>
        ) : (
          <>
            <Rule icon={Target} title={t("rule_exact")} desc={t("rule_exact_desc")} pts={5} />
            <Rule icon={ArrowsLeftRight} title={t("rule_diff")} desc={t("rule_diff_desc")} pts={3} />
            <Rule icon={House} title={t("rule_home")} desc={t("rule_home_desc")} pts={1} />
            <Rule icon={Airplane} title={t("rule_away")} desc={t("rule_away_desc")} pts={1} />
          </>
        )}
        <Rule icon={Trophy} title={t("rule_bonus")} desc={t("rule_bonus_desc")} pts={20} />
      </div>

      <div className="mt-5 bg-neutral-50 border border-neutral-200 rounded-2xl p-4">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral-500 mb-2">{t("examples")}</div>
        <ul className="text-xs text-neutral-600 space-y-1.5">
          {isFourPoint ? (
            <>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 2:1</span><span className="font-mono-tab text-[#41a42b] font-bold">+4</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 4:3 ({t("example_diff_winner")})</span><span className="font-mono-tab font-bold">+2</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 4:1 ({t("example_away_winner")})</span><span className="font-mono-tab font-bold">+2</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 4:2 ({t("example_winner")})</span><span className="font-mono-tab font-bold">+1</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:2 · {t("tip")} 1:1 ({t("example_diff_tie")})</span><span className="font-mono-tab font-bold">+2</span></li>
            </>
          ) : (
            <>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 2:1</span><span className="font-mono-tab text-[#41a42b] font-bold">+5</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 3:2</span><span className="font-mono-tab font-bold">+3</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 2:0</span><span className="font-mono-tab font-bold">+1</span></li>
              <li className="flex justify-between"><span>{t("actual")} 2:1 · {t("tip")} 0:0</span><span className="font-mono-tab text-neutral-400">0</span></li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}
